import {
  MenuOptionGroupProps as ChakraMenuOptionGroupProps,
  useMenuOptionGroup,
} from '@chakra-ui/react';
import classnames from 'classnames';
import React from 'react';

import { OmitChakraProps } from '../../utils/typeUtils';
import { MenuGroup } from '../MenuGroup/MenuGroup';

export interface MenuOptionGroupProps<T extends string | string[]>
  extends OmitChakraProps<
    ChakraMenuOptionGroupProps,
    'title' | 'value' | 'defaultValue' | 'onChange'
  > {
  /**
   * A label describing the menu group. This is pretty much the equivalent of
   * MenuGroup.title, but we found it to be too limiting for Orbit's use cases.
   * This way we can prevent the use of title to avoid rendering the original.
   */
  label?: React.ReactNode;
  /**
   * A short description below the title that gives further context.
   */
  labelDescription?: React.ReactNode;

  /**
   * The selected value(s) for the entire `MenuOptionGroup`
   *
   * NOTE: This is required to control the state of `MenuItemOption` children
   * due to internal Chakra mechanics!
   */
  value: T | undefined;

  /**
   * The onChange handler that gets passed down to the `MenuItemOption` children
   *
   * NOTE: This is required to control the state of `MenuItemOption` children
   * due to internal Chakra mechanics!
   */
  onChange: (value: T) => void;
}

/**
 * Should be used with `MenuItemOption` elements to create a grouping of controls
 */
export const MenuOptionGroup = <T extends string | string[]>(
  props: MenuOptionGroupProps<T>,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ownProps = useMenuOptionGroup(props as any);
  return (
    <MenuGroup
      {...ownProps}
      label={props.label}
      labelDescription={props.labelDescription}
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={classnames('orbit-menu__option-group', props.className)}
    />
  );
};

MenuOptionGroup.displayName = 'MenuOptionGroup';
